// T020 — Issue Credit dialog (US3).
//
// Two modes (single component, single schema — no form drift):
//
// 1. `fixedAccount` — the operator is already on a wallet detail page. We
//    show the account email/emaid as context, no picker.
// 2. otherwise — the dialog is opened from the listing page as a top-level
//    cold-start "Issue credit" entry. We render an account picker
//    (PrimeReact AutoComplete against the existing accountApi user list)
//    and require the operator to pick a target account before submit. This
//    is the only path that works for accounts that have no wallet row yet
//    (Payment team confirmed lazy-creation in the issuance handler).
import { useCallback, useEffect, useMemo, useState } from "react";
import { AutoComplete, AutoCompleteCompleteEvent } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";

import { issueCreditSchema } from "../validation";
import { endOfBerlinDayFromPicked } from "../../../utils/berlinTime";
import { parseEurInput } from "../../../utils/eur";
import { getAllCochargeUsers } from "../../../services/owners";
import type { AdminIssueRequest } from "../../../types/wallet";

export interface IssueAccountOption {
  emaid: string;
  email: string;
  displayName?: string;
}

interface FixedAccount {
  accountId: string;
  email: string;
}

interface IssueCreditDialogProps {
  visible: boolean;
  fixedAccount?: FixedAccount;
  loading: boolean;
  onSubmit: (input: { accountId: string; request: AdminIssueRequest }) => Promise<void>;
  onHide: () => void;
}

const TOMORROW = (() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
})();

// `crypto.randomUUID` needs a secure context; fall back to random bytes so a
// plain-http dev host still gets a unique id.
const newRequestId = (): string => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
};

const matchAccount = (option: IssueAccountOption, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    option.emaid.toLowerCase().includes(q) ||
    option.email.toLowerCase().includes(q) ||
    (option.displayName?.toLowerCase().includes(q) ?? false)
  );
};

const IssueCreditDialog = ({
  visible,
  fixedAccount,
  loading,
  onSubmit,
  onHide,
}: IssueCreditDialogProps) => {
  const [amount, setAmount] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  // One id per opening of the dialog: retrying the same submission (e.g.
  // after a timeout) reuses it, so the backend replays instead of crediting
  // twice. Reopening the dialog starts a new issuance.
  const [requestId, setRequestId] = useState<string>(newRequestId);

  // Account picker state (only used when fixedAccount is undefined).
  const [accountPick, setAccountPick] = useState<IssueAccountOption | string | null>(null);
  const [allAccounts, setAllAccounts] = useState<IssueAccountOption[] | null>(null);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<IssueAccountOption[]>([]);

  // Lazily load the account list once the picker is needed. `accountsLoading`
  // is intentionally NOT a dep here: putting it in the dep array causes the
  // effect to re-fire when we flip the flag on, the previous run's cleanup sets
  // `cancelled = true`, and the resolving fetch then skips the `setAccountsLoading(false)`
  // call — leaving the field stuck on "Loading accounts" forever.
  useEffect(() => {
    if (!visible || fixedAccount || allAccounts !== null) return;
    let cancelled = false;
    setAccountsLoading(true);
    getAllCochargeUsers()
      .then((users) => {
        if (cancelled) return;
        const options: IssueAccountOption[] = (users ?? [])
          .filter((u: any) => Boolean(u?.emaid))
          .map((u: any) => ({
            emaid: String(u.emaid),
            email: String(u.email ?? ""),
            displayName: u.name ?? u.nickname ?? undefined,
          }));
        setAllAccounts(options);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load accounts for issue picker", err);
          setAllAccounts([]);
        }
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, fixedAccount, allAccounts]);

  useEffect(() => {
    if (visible) {
      setRequestId(newRequestId());
    } else {
      setAmount(null);
      setExpiresAt(null);
      setNote("");
      setError(null);
      setAccountPick(null);
      setSuggestions([]);
    }
  }, [visible]);

  const completeAccount = useCallback(
    (event: AutoCompleteCompleteEvent) => {
      const pool = allAccounts ?? [];
      const filtered = pool.filter((opt) => matchAccount(opt, event.query)).slice(0, 25);
      setSuggestions(filtered);
    },
    [allAccounts],
  );

  const resolvedAccountId: string | null = useMemo(() => {
    if (fixedAccount) return fixedAccount.accountId;
    if (accountPick && typeof accountPick === "object") return accountPick.emaid;
    if (typeof accountPick === "string" && accountPick.trim()) return accountPick.trim();
    return null;
  }, [accountPick, fixedAccount]);

  const headerNote = fixedAccount ? (
    <p className="text-sm text-color-secondary mt-0">
      Manually issue credits to <strong>{fixedAccount.email}</strong>. The action is
      recorded in the wallet&apos;s audit log with your identity and the note below.
    </p>
  ) : (
    <p className="text-sm text-color-secondary mt-0">
      Issue credits to any account by selecting it below. If the account has no
      wallet yet, one is created automatically on first issuance.
    </p>
  );

  const handleSubmit = async () => {
    setError(null);
    if (!resolvedAccountId) {
      setError("Pick a target account first.");
      return;
    }
    try {
      const validated = await issueCreditSchema.validate({
        amount,
        expires_at: expiresAt,
        note,
      });
      const request: AdminIssueRequest = {
        amount: parseEurInput(validated.amount as number),
        currency: "EUR",
        // "Valid until 31.12." means through the end of that day in Berlin,
        // not the local midnight the picker returns.
        expires_at: endOfBerlinDayFromPicked(validated.expires_at as Date).toISOString(),
        note: (validated.note as string).trim(),
        request_id: requestId,
      };
      await onSubmit({ accountId: resolvedAccountId, request });
    } catch (err: any) {
      setError(err?.message ?? "Could not issue credits. Please try again.");
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label="Cancel" severity="secondary" text onClick={onHide} disabled={loading} />
      <Button
        label="Issue credits"
        icon="pi pi-plus-circle"
        onClick={handleSubmit}
        loading={loading}
        disabled={!resolvedAccountId}
      />
    </div>
  );

  return (
    <Dialog
      header="Issue credits"
      visible={visible}
      onHide={onHide}
      style={{ width: "32rem" }}
      footer={footer}
      modal
      blockScroll
      closable={!loading}
    >
      {headerNote}

      {!fixedAccount && (
        <div className="field">
          <label htmlFor="issue-account" className="font-medium">Account</label>
          <AutoComplete
            inputId="issue-account"
            value={accountPick}
            suggestions={suggestions}
            completeMethod={completeAccount}
            field="emaid"
            itemTemplate={(option: IssueAccountOption) => (
              <div className="flex flex-column">
                <span className="font-medium">{option.email || option.emaid}</span>
                <span className="text-sm text-color-secondary">{option.emaid}</span>
              </div>
            )}
            onChange={(event) => setAccountPick(event.value)}
            placeholder={
              accountsLoading
                ? "Loading accounts…"
                : "Search by email or emaid"
            }
            disabled={accountsLoading}
            forceSelection
            dropdown
            className="w-full mt-1"
          />
        </div>
      )}

      <div className="field">
        <label htmlFor="issue-amount" className="font-medium">Amount (EUR)</label>
        <InputNumber
          inputId="issue-amount"
          value={amount}
          onValueChange={(event) => setAmount(event.value ?? null)}
          mode="currency"
          currency="EUR"
          locale="de-DE"
          minFractionDigits={2}
          maxFractionDigits={2}
          min={0.01}
          showButtons
          className="w-full mt-1"
        />
      </div>

      <div className="field">
        <label htmlFor="issue-expires-at" className="font-medium">Expiration date</label>
        <Calendar
          inputId="issue-expires-at"
          value={expiresAt}
          onChange={(event) => setExpiresAt((event.value as Date) ?? null)}
          minDate={TOMORROW}
          dateFormat="yy-mm-dd"
          showIcon
          className="w-full mt-1"
        />
      </div>

      <div className="field">
        <label htmlFor="issue-note" className="font-medium">Note (required)</label>
        <InputTextarea
          id="issue-note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          placeholder='e.g. "comp for failed session CDR-7788"'
          className="w-full mt-1"
        />
      </div>

      {error && <Message severity="error" text={error} className="w-full" />}
    </Dialog>
  );
};

export default IssueCreditDialog;
